import { useEffect, useState } from 'react';
import { DateTime, DurationLikeObject } from 'luxon';
import { toast } from 'react-toastify';
import { useMutation } from '@tanstack/react-query';
import {
  Box,
  CardContent,
  FormControl,
  Grid,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import DashboardTimeRange from '@bumble/app/components/DashboardTimeRange';
import { DASHBOARD_TIME_BUTTONS } from '@bumble/app/constants';
import { useApi } from '@bumble/app/hooks/use-api';
import { getTooltipLabel } from '@bumble/app/utils/get-date';
import { apiError } from '@bumble/app/utils/api-error';
import { useCpe } from './CpeContext';
import { Loading } from '../Loading';

interface LinkUtilizationDashboardProps {
  selectedTab: string;
}

export const LinkUtilizationDashboard = ({selectedTab}: LinkUtilizationDashboardProps) => {
  const [timeRange, setTimeRange] = useState<DurationLikeObject>(DASHBOARD_TIME_BUTTONS[0].value);
  const { cpe, setCpe } = useCpe();
  const [chartData, setChartData] = useState<any[]>([]);
  const [chartData4, setChartData4] = useState<any[]>([]);
  const [isLoading1, setIsLoading1] = useState<boolean>(false);
  const [isLoading2, setIsLoading2] = useState<boolean>(false);
  const [utilizationSetting, setUtilizationSetting] = useState<'peak' | 'average'>('peak');
  const api = useApi();
  let oldCpeName: string, oldTimeRange: DurationLikeObject;

  const colors = ['#ECC052', '#88847D', '#5F467E', '#7EB670'];
  const utilizationSettings = [
    { value: 'peak', label: 'Peak Rate' },
    { value: 'average', label: 'Average Rate' },
  ];

  const apiCall1 = useMutation(api.dashboardGetNodePeakAvgUtilIndividualApiV1DashboardGetNodePeakAvgUtilIndividualGet, {
    onSuccess(data) {
      setIsLoading1(false);
      setChartData(data.data.map(n => ({
        rx_avg_util: parseFloat(n.rx_avg_util) * 100,
        rx_peak_util: parseFloat(n.rx_peak_util) * 100,
        timestamp: new Date(n.timestamp).getTime(),
        tx_avg_util: parseFloat(n.tx_avg_util) * 100,
        tx_peak_util: parseFloat(n.tx_peak_util) * 100,
      })).sort((a: any, b: any) => a.timestamp - b.timestamp));
    },
    onError(e) {
      setIsLoading1(false);
      apiError(e);
      setChartData([]);
    },
  });

  const apiCall2 = useMutation(api.dashboardGetPeakHoursApiV1DashboardGetPeakHoursGet, {
    onSuccess(data) {
      const fullData = (data.data as any).peak_hours;
      const rxArr: any[] = [], txArr: any[] = [];
      const diffHour = new Date().getTimezoneOffset() / 60;
      for (let i in fullData.peak_hour_scatter_plot['rx.mbps']) {
        const j = (parseInt(i) + 24 - diffHour) % 24;
        fullData.peak_hour_scatter_plot['rx.mbps'][i].forEach((n: number) => {
          rxArr.push({ x: j * 3600, y: n, z: 200 });
        });
      }
      for (let i in fullData.peak_hour_scatter_plot['tx.mbps']) {
        const j = (parseInt(i) + 24 - diffHour) % 24;
        fullData.peak_hour_scatter_plot['tx.mbps'][i].forEach((n: number) => {
          txArr.push({ x: j * 3600, y: n, z: 200 });
        });
      }
      setChartData4(fullData.peak_throughput_trend.map((n: any) => ({
            max_rx_bps: n['max.rx.bps'],
            max_tx_bps: n['max.tx.bps'],
            timestamp: new Date(n.timestamp).getTime(),
        })).sort((a: any, b: any) => a.timestamp - b.timestamp));
      setIsLoading2(false);
    },
    onError(e) {
      setIsLoading2(false);
      apiError(e);
    },
  });

  const handleTimeChange = (e: React.MouseEvent<HTMLElement>, val: DurationLikeObject) => {
    if (val) setTimeRange(val);
  };

  const handleCpeChange = (val: any) => {
    if (val) {
      setCpe({
        cpeName: val.label,
        nodeId: val.id,
      });
    }
  };

  useEffect(() => {
    if (cpe.cpeName && cpe.cpeName !== oldCpeName) {
      setIsLoading2(true);
      apiCall2.mutate({
        node_id: cpe.nodeId,
        node_name: cpe.cpeName,
      });
      oldCpeName = cpe.cpeName;
    }
  }, [cpe]);

  useEffect(() => {
    if (cpe.cpeName && timeRange && (cpe.cpeName !== oldCpeName || timeRange !== oldTimeRange)) {
      setIsLoading1(true);
      apiCall1.mutate({
        node_id: cpe.nodeId,
        node_name: cpe.cpeName,
        start_time: DateTime.now().minus(timeRange).toISO(),
        end_time: DateTime.now().toISO(),
      });
      oldCpeName = cpe.cpeName;
      oldTimeRange = timeRange;
    }
  }, [timeRange, cpe])

  const handleRefreshClick = () => {
    if (cpe.cpeName && timeRange) {
      setIsLoading1(true);
      apiCall1.mutate({
        node_id: cpe.nodeId,
        node_name: cpe.cpeName,
        start_time: DateTime.now().minus(timeRange).toISO(),
        end_time: DateTime.now().toISO(),
      });
      setIsLoading2(true);
      apiCall2.mutate({
        node_id: cpe.nodeId,
        node_name: cpe.cpeName,
      });
    }
    toast.success('Refreshed chart data.');
  };

  const CustomizedTicks = () => {
    const days = timeRange.days || 0;
    const totalHours = timeRange.hours || days * 24;

    if (days >= 30) {
      return {
        tickFormatter: (v: number) =>
          DateTime.fromMillis(v).toFormat('MMM d'),
          height: 30,
      };
    }

    if (totalHours <= 3) {
      return {
        tickFormatter: (v: number) =>
          DateTime.fromMillis(v).toFormat('HH:mm'),
          height: 30,
      };
    }

    return {
      tick: ({ x, y, payload }: any) => {
        const date = DateTime.fromMillis(payload.value);

        return (
          <g transform={`translate(${x},${y})`}>
            <text x={0} y={0} dy={16} fill="#666">
              <tspan textAnchor="middle" x="0" dy="15">
                {date.toFormat('MMM d')}
              </tspan>
              <tspan textAnchor="middle" x="0" dy="20">
                {date.toFormat('HH:mm')}
                </tspan>
            </text>
          </g>
        );
      },
      height: 60,
    };
  };

  return (
    <Box>
      <Box sx={{ mb: 0, py: 5 }}>
        <DashboardTimeRange
          timeValues={DASHBOARD_TIME_BUTTONS}
          selectedTime={timeRange}
          onCpeChange={handleCpeChange}
          onTimeChange={handleTimeChange}
          onRefresh={handleRefreshClick}
          selectedTab={selectedTab}
        />
      </Box>
      <CardContent sx={{bgcolor: '#fff', borderRadius: 1.2, mb: 5}}>
        <Grid container spacing={5} sx={{ marginTop: 1 }}>
          <Grid item xs={12} md={12}>
            <Box>
              <Box sx={{ display: 'inline-flex', justifyContent: 'center', alignItems: 'center', mb: 4, flexWrap: 'wrap', gap: 4 }}>
                <Typography color="primary">Upload/Download Utilization (%)</Typography>
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <Select
                    value={utilizationSetting}
                    onChange={(e) => setUtilizationSetting(e.target.value as 'peak' | 'average')}
                    sx={(theme) => ({
                      height: 32,
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: `${theme.palette.divider} !important` },
                      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: `${theme.palette.divider} !important` },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: `${theme.palette.divider} !important` },
                    })}
                  >
                    {utilizationSettings.map(({ value, label }) => (
                      <MenuItem key={value} value={value}>{label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              {isLoading1 ? <Loading /> :
              <ResponsiveContainer width="100%" height={250}>
                <LineChart
                  margin={{ top: 5, right: 10, left: 20, bottom: 5, }}
                  data={chartData}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="timestamp"
                    type="number"
                    scale="time"
                    domain={['dataMin', 'dataMax']}
                    {...CustomizedTicks()}
                    minTickGap={40}
                  />
                  <YAxis
                    orientation="left"
                    type="number"
                    domain={[0, 100]}
                    width={30}
                    tickFormatter={(value: number) => Number(value).toFixed(1)}
                  />
                  <Line
                    strokeWidth={2}
                    stroke={colors[0]}
                    dot={false}
                    dataKey={utilizationSetting === 'peak' ? 'tx_peak_util' : 'tx_avg_util'}
                    name="Upload"
                  />
                  <Line
                    strokeWidth={2}
                    stroke={colors[2]}
                    dot={false}
                    dataKey={utilizationSetting === 'peak' ? 'rx_peak_util' : 'rx_avg_util'}
                    name="Download"
                  />
                  <Tooltip
                    labelFormatter={(label) => getTooltipLabel(label)}
                    formatter={(value: number, name: string) => [`${Number(value).toFixed(1)} %`, name]}
                  />
                  <Legend wrapperStyle={{ bottom: -5 }} iconType={'rect'} />
                </LineChart>
              </ResponsiveContainer>}
            </Box>
          </Grid>
        </Grid>
      </CardContent>
      <CardContent sx={{ bgcolor: '#fff', borderRadius: 1.2, mb: 5 }}>
        <Typography color="primary" sx={{ mb: 2 }}>Peak Hour Throughput Trend (Mbps)</Typography>
        {isLoading2 ? <Loading height={250} /> :
          <ResponsiveContainer width="100%" height={250}>
            <LineChart
              margin={{ top: 5, right: 10, left: 20, bottom: 5 }}
              data={chartData4}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="timestamp"
                type="number"
                scale="time"
                domain={['dataMin', 'dataMax']}
                tickFormatter={(v) =>
                  DateTime.fromMillis(v).toFormat('MMM d')
                }
              />
              <YAxis
                orientation="left"
                type="number"
                width={30}
                tickFormatter={(value: number) => `${new Intl.NumberFormat('en-US', {
                  notation: 'compact',
                  compactDisplay: 'short',
                }).format(value)}`}
              />
              <Line strokeWidth={2} stroke={colors[0]} dot={false} dataKey="max_tx_bps" name="Upload" />
              <Line strokeWidth={2} stroke={colors[2]} dot={false} dataKey="max_rx_bps" name="Download" />
              <Tooltip labelFormatter={(label) => getTooltipLabel(label)} />
              <Legend wrapperStyle={{ bottom: -5 }} iconType={'rect'} />
            </LineChart>
          </ResponsiveContainer>
        }
      </CardContent>
    </Box>
  );
};
